/**
 * The shell's navigation manifest — the sidebar/drawer list and the profile
 * menu, as data.
 *
 * Lives in `shared` for the same reason the settings groups do: web and mobile
 * render the same destinations, and when each platform kept its own copy they
 * drifted. Between 2026-07-13 and 2026-08-27 the web sidebar gained Goals,
 * Measurements, Reports and a role-gated Clients, regrouped its sections, and
 * the profile menu gained Friends and Trainers — while the mobile copies stood
 * still and kept offering a Messages route that does not exist. Order, labels,
 * grouping and role gates are one definition now; a platform can only differ
 * where it says so out loud, via {@link NavItem.platforms}.
 *
 * Deliberately data-only — no JSX, no icon components, no router imports — so
 * it can be read by a React web app and a React Native app alike. {@link
 * NavIconName} is a name each platform maps to its own icon layer (heroicons on
 * web, the `Glyph` registry on mobile), which is the same shape the settings
 * nav already uses.
 *
 * Import by subpath (`shared/src/config/nav.config`), never through the shared
 * root barrel — that barrel is not React Native-safe.
 */

import type { Role } from '../types/auth';
import type { DashboardProfile } from '../types/dashboard.types';

/**
 * An icon, by name rather than by component.
 *
 * The vocabulary is heroicons v2 outline names in camelCase, matching the
 * mobile `Glyph` registry. Kept to the names navigation actually uses so that a
 * typo is a type error and each platform's map can be exhaustively checked.
 */
export type NavIconName =
  | 'adjustmentsHorizontal'
  | 'archiveBox'
  | 'arrowRightOnRectangle'
  | 'bell'
  | 'bolt'
  | 'calendar'
  | 'chartBar'
  | 'clipboardDocumentList'
  | 'cog6Tooth'
  | 'fire'
  | 'flag'
  | 'home'
  | 'newspaper'
  | 'questionMarkCircle'
  | 'scale'
  | 'ticket'
  | 'trophy'
  | 'user'
  | 'userGroup'
  | 'users';

/** A surface that renders navigation. */
export type NavPlatform = 'web' | 'mobile';

/** A tappable destination. */
export interface NavItem {
  kind: 'item';
  id: string;
  label: string;
  /**
   * One word for a surface too narrow for {@link label}: the mobile Me hub's
   * tiles, four to a row. Omitted where the label already is one. Read it
   * through {@link navShortLabel}.
   */
  shortLabel?: string;
  /** Absolute path. Web routes and expo-router paths agree on these. */
  href: string;
  icon: NavIconName;
  /**
   * Roles that see the item. Omitted means every role.
   *
   * A gate here is a product rule, not a cosmetic filter — the entries that
   * carry one lead to endpoints that refuse the other role outright.
   */
  roles?: readonly Role[];
  /**
   * Platforms that render the item. Omitted means both.
   *
   * Only for differences that are decided, not accidental: Sign Out is a web
   * affordance because mobile puts signing out in Settings, where a rare
   * destructive action belongs on a phone.
   */
  platforms?: readonly NavPlatform[];
  /** Destructive styling. */
  danger?: boolean;
  /** Keyboard hint. Rendered where a keyboard exists. */
  shortcut?: string;
}

/** A group separator, with an optional section heading. */
export interface NavDivider {
  kind: 'divider';
  id: string;
  label?: string;
}

export type NavEntry = NavItem | NavDivider;

/** Narrow a {@link NavEntry} to a destination. */
export function isNavItem(entry: NavEntry): entry is NavItem {
  return entry.kind === 'item';
}

// =============================================================================
// SIDEBAR — the primary navigation (web sidebar, mobile drawer)
// =============================================================================

/**
 * Grouped by what you do with a destination, rather than by what it is.
 *
 * The training band comes first — the things you act on. Then Progress, the
 * reading half: what you are aiming at, what your body is doing, what it all
 * means. Then Library, the only section that is not about you; it replaced
 * "Resources", a label that had grown to mean "everything left over" and put a
 * catalogue you consult beside a view of your own sessions.
 *
 * Settings and Help & Support are not here. They are account destinations, and
 * the profile menu already carries both; listing them twice cost a trainer's
 * sidebar the last of its height, so it scrolled on a laptop window.
 */
export const SIDEBAR_NAV: readonly NavEntry[] = [
  /**
   * Web only, by decision. On a phone the Home tab is the dashboard, so a
   * Dashboard tile in the Me hub would be a second door to the same room.
   */
  {
    kind: 'item',
    id: 'dashboard',
    label: 'Dashboard',
    href: '/dashboard',
    icon: 'home',
    platforms: ['web'],
  },
  /**
   * A trainer's roster leads the training band the way My Plans leads a
   * client's, because it is their primary work surface. Deliberately here
   * rather than in the profile menu: Friends and Trainers are account
   * destinations you visit occasionally to manage a relationship, whereas this
   * is a daily worklist — and the mobile tab bar already treats it as primary.
   */
  {
    kind: 'item',
    id: 'clients',
    label: 'Clients',
    href: '/clients',
    icon: 'users',
    roles: ['trainer'],
  },
  {
    kind: 'item',
    id: 'my-plans',
    label: 'My Plans',
    shortLabel: 'Plans',
    href: '/plans',
    icon: 'clipboardDocumentList',
  },
  {
    kind: 'item',
    id: 'my-workouts',
    label: 'My Workouts',
    shortLabel: 'Workouts',
    href: '/workouts',
    icon: 'bolt',
  },
  {
    kind: 'item',
    id: 'challenges',
    label: 'Challenges',
    href: '/challenges',
    icon: 'trophy',
  },
  /**
   * Calendar sits with the sessions it schedules, not beside a catalogue of
   * exercises: it is a view of your own training, not reference material.
   */
  {
    kind: 'item',
    id: 'calendar',
    label: 'Calendar',
    href: '/calendar',
    icon: 'calendar',
  },
  { kind: 'divider', id: 'progress-divider', label: 'Progress' },
  { kind: 'item', id: 'goals', label: 'Goals', href: '/goals', icon: 'flag' },
  {
    kind: 'item',
    id: 'measurements',
    label: 'Measurements',
    // The word the analytics tab bar already uses for this destination.
    shortLabel: 'Body',
    href: '/measurements',
    icon: 'scale',
  },
  {
    kind: 'item',
    id: 'reports',
    label: 'Reports',
    href: '/reports',
    icon: 'chartBar',
  },
  { kind: 'divider', id: 'library-divider', label: 'Library' },
  {
    kind: 'item',
    id: 'exercise-library',
    label: 'Exercise Library',
    shortLabel: 'Exercises',
    href: '/exercises',
    icon: 'archiveBox',
  },
];

// =============================================================================
// PROFILE MENU — the avatar dropdown (web) / user sheet (mobile)
// =============================================================================

/**
 * Grouped by what an item is *about*: identity, then the people you have
 * granted access to, then the account itself, then support, then leaving.
 *
 * Send Feedback and Messages are absent on purpose. Neither `/feedback` nor
 * `/messages` is a route in this app, and no messaging system was ever built —
 * mobile carried both as dead rows for six weeks. They belong here only once
 * something can receive them.
 */
export const PROFILE_MENU_NAV: readonly NavEntry[] = [
  /**
   * Web only, by decision. On mobile the Me hub opens with your own profile
   * row, which does this row's job in the hub's own shape.
   */
  {
    kind: 'item',
    id: 'profile',
    label: 'View Profile',
    href: '/profile',
    icon: 'user',
    platforms: ['web'],
  },
  { kind: 'divider', id: 'access-divider' },
  /**
   * People who hold access to your training. Friends and Trainers are both
   * access grants you manage occasionally — which is what makes them account
   * destinations rather than sidebar ones. A trainer's Clients is the
   * opposite: a daily worklist, and it lives in the sidebar.
   *
   * No badge on Friends: the bell already carries friend-request
   * notifications, and a second alert surface would split one signal across
   * two places.
   */
  {
    kind: 'item',
    id: 'friends',
    label: 'Friends',
    href: '/friends',
    icon: 'userGroup',
  },
  /**
   * Clients only, and that gate matches the server rather than guessing:
   * registration assigns exactly one role, `GET /api/trainer` answers an empty
   * list for anyone who is not a client, and `POST /api/trainer` is role-gated
   * outright. A trainer cannot hire a trainer.
   */
  {
    kind: 'item',
    id: 'trainers',
    label: 'Trainers',
    href: '/trainers',
    icon: 'users',
    roles: ['client'],
  },
  { kind: 'divider', id: 'account-divider' },
  /**
   * Web only, by decision. On mobile the header bell is the one way to the
   * inbox; a hub row would be a second.
   */
  {
    kind: 'item',
    id: 'notifications',
    label: 'Notifications',
    href: '/notifications',
    icon: 'bell',
    platforms: ['web'],
  },
  {
    kind: 'item',
    id: 'settings',
    label: 'Settings',
    href: '/settings',
    icon: 'cog6Tooth',
    shortcut: '⌘,',
  },
  {
    kind: 'item',
    id: 'adjust-goals',
    label: 'Adjust Goals',
    href: '/questionnaire',
    icon: 'adjustmentsHorizontal',
  },
  { kind: 'divider', id: 'support-divider' },
  {
    kind: 'item',
    id: 'help',
    label: 'Help & Support',
    href: '/help',
    icon: 'questionMarkCircle',
  },
  /**
   * Opens the invite dialog rather than a page: web answers it inside the
   * dropdown, as it does Adjust Goals, and mobile pushes its `/invite` card.
   */
  {
    kind: 'item',
    id: 'invite',
    label: 'Invite to Dō',
    href: '/invite',
    icon: 'ticket',
  },
  { kind: 'divider', id: 'session-divider' },
  /**
   * Web only, by decision. On a phone this is a rare, destructive action and
   * it lives in Settings; putting it one tap from the avatar is a desktop
   * convention that does not transfer.
   */
  {
    kind: 'item',
    id: 'sign-out',
    label: 'Sign Out',
    href: '/logout',
    icon: 'arrowRightOnRectangle',
    platforms: ['web'],
    danger: true,
  },
];

// =============================================================================
// TAB BAR — the phone's bottom bar (web below md, the mobile app)
// =============================================================================

/**
 * The dashboard profiles the tab bar has a layout for. `compact` is a density,
 * not an interest, so it borrows the default bar.
 */
export type TabBarProfile = Extract<
  DashboardProfile,
  'default' | 'social' | 'analytics' | 'trainer'
>;

/** First tab for everyone. */
const TAB_HOME: NavItem = {
  kind: 'item',
  id: 'home',
  label: 'Home',
  href: '/dashboard',
  icon: 'home',
};

/** Last tab for everyone. */
const TAB_ME: NavItem = {
  kind: 'item',
  id: 'me',
  label: 'Me',
  href: '/profile',
  icon: 'user',
};

/**
 * The two tabs either side of Start Workout, chosen by the dashboard profile
 * the user picked, so the bar points where the dashboard does.
 *
 * Challenges used to hold a slot for everyone. It compares you with other
 * people, and the app is about your own progress, so it now appears only for
 * users who picked the Social profile. Default is the bar most users never
 * change, and it holds your workouts and your goals.
 */
export const TAB_BAR_SLOTS: Readonly<
  Record<TabBarProfile, readonly [NavItem, NavItem]>
> = {
  default: [
    {
      kind: 'item',
      id: 'workouts',
      label: 'Workouts',
      href: '/workouts',
      icon: 'fire',
    },
    { kind: 'item', id: 'goals', label: 'Goals', href: '/goals', icon: 'flag' },
  ],
  social: [
    {
      kind: 'item',
      id: 'challenges',
      label: 'Challenges',
      href: '/challenges',
      icon: 'trophy',
    },
    {
      kind: 'item',
      id: 'feed',
      label: 'Feed',
      href: '/feed',
      icon: 'newspaper',
    },
  ],
  analytics: [
    {
      kind: 'item',
      id: 'reports',
      label: 'Reports',
      href: '/reports',
      icon: 'chartBar',
    },
    // "Measurements" does not fit a fifth of a phone at 10pt; the drawer keeps it.
    {
      kind: 'item',
      id: 'measurements',
      label: 'Body',
      href: '/measurements',
      icon: 'scale',
    },
  ],
  trainer: [
    {
      kind: 'item',
      id: 'clients',
      label: 'Clients',
      href: '/clients',
      icon: 'users',
      roles: ['trainer'],
    },
    {
      kind: 'item',
      id: 'plans',
      label: 'Plans',
      href: '/plans',
      icon: 'clipboardDocumentList',
      roles: ['trainer'],
    },
  ],
};

/**
 * The bar a stored `dashboard_profile` gets. A missing or `compact` profile
 * gets the default bar, and so does a `trainer` profile on an account that is
 * no longer a trainer, so a stale preference cannot open the roster.
 */
export function resolveTabBarProfile(
  profile: DashboardProfile | null | undefined,
  role: Role | undefined
): TabBarProfile {
  if (!profile || profile === 'compact') return 'default';
  if (profile === 'trainer' && role !== 'trainer') return 'default';
  return profile;
}

/**
 * The four tabs, in order: Home, the profile's two, then Me. The center
 * Start Workout button is not a destination, so each platform draws it between
 * the second and third.
 */
export function resolveTabBar(
  profile: DashboardProfile | null | undefined,
  role: Role | undefined
): NavItem[] {
  const [left, right] = TAB_BAR_SLOTS[resolveTabBarProfile(profile, role)];
  return [TAB_HOME, left, right, TAB_ME];
}

// =============================================================================
// ME HUB — the mobile Me tab, which replaced the drawer and the user sheet
// =============================================================================

/**
 * How the Me hub regroups the profile menu, by item id.
 *
 * The hub keeps the sidebar's destinations as one grid of places and folds the
 * rest of the profile menu into two short lists, so the whole screen is about
 * a screen and a half. Invite joins the people you have granted access to,
 * because it is how more of them arrive; Help joins the account. Web's dropdown
 * keeps its own grouping, which is why this is a mapping and not a reorder of
 * {@link PROFILE_MENU_NAV}.
 */
export const ME_HUB_GROUPS = {
  people: ['friends', 'trainers', 'invite'],
  account: ['settings', 'adjust-goals', 'help'],
} as const;

/** The Me hub's three groups, each in render order. */
export interface MeHub {
  /** The sidebar's destinations, drawn as tiles. */
  places: NavItem[];
  people: NavItem[];
  account: NavItem[];
}

/**
 * What a viewer's Me hub shows. Role gates and platform declarations come
 * from the two manifests, so a trainer gets Clients among the places and no
 * Trainers row, exactly as the lists they replace did.
 */
export function resolveMeHub(role: Role | undefined): MeHub {
  const context: NavContext = { role, platform: 'mobile' };
  const menu = resolveNavItems(PROFILE_MENU_NAV, context);
  const pick = (groupIds: readonly string[]): NavItem[] =>
    groupIds.flatMap((id) => menu.filter((item) => item.id === id));

  return {
    places: resolveNavItems(SIDEBAR_NAV, context),
    people: pick(ME_HUB_GROUPS.people),
    account: pick(ME_HUB_GROUPS.account),
  };
}

/** The one-word name a narrow surface draws, or the full label. */
export function navShortLabel(item: NavItem): string {
  return item.shortLabel ?? item.label;
}

// =============================================================================
// RESOLUTION
// =============================================================================

export interface NavContext {
  /** The viewer's role. `undefined` while the session is still loading. */
  role?: Role | undefined;
  platform: NavPlatform;
}

/** Whether an item is visible to this viewer on this surface. */
function isVisible(item: NavItem, { role, platform }: NavContext): boolean {
  if (item.platforms && !item.platforms.includes(platform)) return false;
  // An unknown role sees only ungated items — better a brief absence than a
  // flash of a destination the viewer will be refused.
  if (item.roles && (role === undefined || !item.roles.includes(role))) {
    return false;
  }
  return true;
}

/**
 * Drop dividers that no longer separate anything.
 *
 * Filtering items can strand a divider at either end of the list or leave two
 * back to back — which renders as a heading over nothing, or a doubled rule.
 * Whether a group survives depends on the viewer, so this cannot be settled in
 * the manifest.
 */
function pruneDividers(entries: NavEntry[]): NavEntry[] {
  const pruned: NavEntry[] = [];

  for (const entry of entries) {
    if (entry.kind === 'divider') {
      // Nothing above it yet, or the previous entry was also a divider.
      const previous = pruned[pruned.length - 1];
      if (previous === undefined || previous.kind === 'divider') continue;
    }
    pruned.push(entry);
  }

  // A trailing divider separates the list from nothing.
  if (pruned[pruned.length - 1]?.kind === 'divider') pruned.pop();

  return pruned;
}

/**
 * The entries a given viewer sees on a given surface, in render order.
 *
 * @example
 *   resolveNav(SIDEBAR_NAV, { role: 'trainer', platform: 'web' })
 */
export function resolveNav(
  entries: readonly NavEntry[],
  context: NavContext
): NavEntry[] {
  return pruneDividers(
    entries.filter(
      (entry) => entry.kind !== 'item' || isVisible(entry, context)
    )
  );
}

/** The visible destinations only, with dividers dropped. */
export function resolveNavItems(
  entries: readonly NavEntry[],
  context: NavContext
): NavItem[] {
  return resolveNav(entries, context).filter(isNavItem);
}
